import React, { useState, useEffect, useCallback } from 'react';

import type { ISliderProps } from './Slider.types';
import { Text } from '../Text/Text';
import { Icon } from '../Icon/Icon';
import { IconName } from '../Icon/Icon.type';

type Direction = 'left' | 'right' | 'none';


// Import slider images
import slide1 from '../../assets/images/slide1.png';
import slide2 from '../../assets/images/slide2.png';
import slide3 from '../../assets/images/slide3.png';

// Slider data
const DEFAULT_SLIDER_DATA = [
  {
    image: slide1,
    title: 'Design at the Speed of Thought',
    description: 'Transform ideas into stunning UI mockups instantly with AI-powered design generation. Describe your vision in plain language and watch pixel-perfect interfaces materialize.',
  },
  {
    image: slide2,
    title: 'Code to Cloud in Minutes',
    description: 'Build complete, production-ready web applications with AI-powered development. Deploy containerized applications with database integration, authentication, and Docker orchestration.',
  },
  {
    image: slide3,
    title: 'Enterprise TAP Automation',
    description: 'Accelerate Tyler Technologies platform development with intelligent AI agents. Automatically generate data models, forms, workflows, and reports for FedRAMP-compliant applications.',
  },
];

export const Slider: React.FC<ISliderProps> = ({
  slides = DEFAULT_SLIDER_DATA,
  autoPlayInterval = 5000,
  className = '',
}: ISliderProps) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [previousSlide, setPreviousSlide] = useState(0);
  const [direction, setDirection] = useState<Direction>('none');
  const [isAnimating, setIsAnimating] = useState(false);

  const handleSlideChange = useCallback((nextSlideOrFn: number | ((prev: number) => number), dir: Direction) => {
    if (isAnimating) return;
    
    setDirection(dir);
    setIsAnimating(true);
    setPreviousSlide(currentSlide);
    
    // Calculate next slide
    const nextSlide = typeof nextSlideOrFn === 'function' 
      ? nextSlideOrFn(currentSlide) 
      : nextSlideOrFn;
    
    // Start transition immediately
    setCurrentSlide(nextSlide);
    
    // Reset animation state after transition completes
    setTimeout(() => {
      setIsAnimating(false);
      setDirection('none');
    }, 500); // Match this with transition duration
  }, [isAnimating, currentSlide]);

  // Auto-play functionality
  useEffect(() => {
    const timer = setInterval(() => {
      handleSlideChange((prev) => (prev + 1) % slides.length, 'left');
    }, autoPlayInterval);

    return () => clearInterval(timer);
  }, [slides.length, autoPlayInterval, handleSlideChange]);

  const goToSlide = (index: number) => {
    if (index === currentSlide || isAnimating) return;
    const dir = index > currentSlide ? 'left' : 'right';
    handleSlideChange(index, dir);
  };

  const goToPrevious = () => {
    handleSlideChange((prev) => (prev - 1 + slides.length) % slides.length, 'right');
  };

  const goToNext = () => {
    handleSlideChange((prev) => (prev + 1) % slides.length, 'left');
  };

  // Animation classes for entering slide
  const getEnterAnimationClass = () => {
    if (direction === 'none') return '';
    if (direction === 'left') return 'animate-slide-in-from-right';
    if (direction === 'right') return 'animate-slide-in-from-left';
    return '';
  };

  // Animation classes for exiting slide
  const getExitAnimationClass = () => {
    if (direction === 'none') return '';
    if (direction === 'left') return 'animate-slide-out-to-left';
    if (direction === 'right') return 'animate-slide-out-to-right';
    return '';
  };

  return (
      <div className={`flex flex-col items-center justify-center max-w-xl w-full gap-bg-v2-xl ${className}`}>
        {/* Image */}
        <div className="relative w-full overflow-hidden min-h-[400px]">
          {/* Exiting slide */}
          {isAnimating && previousSlide !== currentSlide && (
            <div 
              className={`absolute inset-0 w-full ${getExitAnimationClass()}`}
            >
              <img
                src={slides[previousSlide].image}
                alt={slides[previousSlide].title}
                className="w-full max-h-[400px] object-contain"
              />
            </div>
          )}
          {/* Entering slide */}
          <div 
            className={`w-full ${getEnterAnimationClass()}`}
          >
            <img
              src={slides[currentSlide].image}
              alt={slides[currentSlide].title}
              className="w-full max-h-[400px] object-contain"
            />
          </div>
        </div>

        {/* Content */}
        <div className="relative flex overflow-hidden min-h-[80px]">
          {/* Exiting title */}
          {isAnimating && previousSlide !== currentSlide && (
            <div 
              className={`absolute inset-0 ${getExitAnimationClass()}`}
            >
              <Text 
                variant="display-sm" 
                weight="semibold"
                color="primary" 
                align="center"
                as="h2"
                className="font-bg-v2-display"
              >
                {slides[previousSlide].title}
              </Text>
            </div>
          )}
          {/* Entering title */}
          <div 
            className={`${getEnterAnimationClass()}`}
          >
            <Text 
              variant="display-sm" 
              weight="semibold"
              color="primary" 
              align="center"
              as="h2"
              className="font-bg-v2-display"
            >
              {slides[currentSlide].title}
            </Text>
          </div>
        </div>
        <div className="relative flex flex-col items-center gap-bg-v2-xl text-center w-full overflow-hidden min-h-[100px]">
          {/* Exiting description */}
          {isAnimating && previousSlide !== currentSlide && (
            <div 
              className={`absolute inset-0 ${getExitAnimationClass()}`}
            >
              <Text 
                variant="text-md" 
                weight="regular" 
                color="secondary" 
                align="center"
                as="p"
                className="font-bg-v2-sans"
              >
                {slides[previousSlide].description}
              </Text>
            </div>
          )}
          {/* Entering description */}
          <div 
            className={`${getEnterAnimationClass()}`}
          >
            <Text 
              variant="text-md" 
              weight="regular" 
              color="secondary" 
              align="center"
              as="p"
              className="font-bg-v2-sans"
            >
              {slides[currentSlide].description}
            </Text>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex items-center gap-bg-v2-xl">
          {/* Previous Button */}
          <button
            onClick={goToPrevious}
            className="w-10 h-10 flex items-center justify-center rounded-bg-v2-full border border-border-secondary bg-bg-primary hover:bg-bg-secondary-hover transition-colors duration-200 focus:outline-none"
            aria-label="Previous slide"
          >
            <Icon name={IconName.Backward} size="xs" variant="secondary" />
          </button>

          {/* Dots Indicator */}
          <div className="flex items-center gap-bg-v2-xs">
            {slides.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`transition-all duration-300 rounded-bg-v2-full focus:outline-none ${
                  index === currentSlide
                    ? 'w-8 h-2 bg-bg-brand-solid'
                    : 'w-2 h-2 bg-bg-quaternary hover:bg-bg-tertiary'
                }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>

          {/* Next Button */}
          <button
            onClick={goToNext}
            className="w-10 h-10 flex items-center justify-center rounded-bg-v2-full border border-border-secondary bg-bg-primary hover:bg-bg-secondary-hover transition-colors duration-200 focus:outline-none"
            aria-label="Next slide"
          >
            <Icon name={IconName.Forward} size="xs" variant="secondary" />
          </button>
        </div>
      </div>
  );
};

